<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useBookmarks } from '../../composables/useBookmarks';
import JobListItemMobile from './JobListItemMobile.vue';
import JobListItemDesktop from './JobListItemDesktop.vue';

const props = defineProps({
  job: { type: Object, required: true },
});

const { t, locale } = useI18n();
const { isBookmarked, toggleBookmark } = useBookmarks();

const jobUrl = computed(() => `/jobs/${props.job.slug || props.job.id}`);

const handleBookmark = () => toggleBookmark(props.job);

const formatTimeAgo = (date) => {
  if (!date) return t('job.time.justNow');
  const now = new Date();
  const past = new Date(date);
  const diffInSeconds = Math.floor((now - past) / 1000);

  if (diffInSeconds < 60) return t('job.time.justNow');

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) return t('job.time.minutesAgo', { count: diffInMinutes });

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return t('job.time.hoursAgo', { count: diffInHours });

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 30) return t('job.time.daysAgo', { count: diffInDays });

  const diffInMonths = Math.floor(diffInDays / 30);
  if (diffInMonths < 12) return t('job.time.monthsAgo', { count: diffInMonths });

  return past.toLocaleDateString(locale.value === 'id' ? 'id-ID' : 'en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const formatSalary = (min, max) => {
  if (!min && !max) return t('job.salary.competitive');
  const localeStr = locale.value === 'id' ? 'id-ID' : 'en-US';
  if (!max) return `${min.toLocaleString(localeStr)}`;
  return `${min.toLocaleString(localeStr)} - ${max.toLocaleString(localeStr)}`;
};
</script>

<template>
  <router-link
    :to="jobUrl"
    class="group block bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-lg p-3 shadow-[0_2px_15px_rgb(0,0,0,0.04)] dark:shadow-none hover:shadow-[0_8px_25px_rgb(0,0,0,0.08)] dark:hover:shadow-lg hover:-translate-y-1 transition-all duration-300 relative overflow-hidden flex flex-col sm:flex-row gap-3"
  >
    <JobListItemMobile
      :job="job"
      :cached-url="job.logo_perusahaan"
      :is-bookmarked="isBookmarked(job.id)"
      :time-ago="formatTimeAgo(job.created_at)"
      :salary="formatSalary(job.gaji_minimal, job.gaji_maksimal)"
      @bookmark="handleBookmark"
    />

    <JobListItemDesktop
      :job="job"
      :cached-url="job.logo_perusahaan"
      :is-bookmarked="isBookmarked(job.id)"
      :time-ago="formatTimeAgo(job.created_at)"
      :salary="formatSalary(job.gaji_minimal, job.gaji_maksimal)"
      @bookmark="handleBookmark"
    />
  </router-link>
</template>
